import { memo, useEffect, useMemo, useState } from "react";
import { Accordion, Badge, Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { Tournament, PlayerLookupResult } from "../../shared/types";
import { StockIcons, type CharacterColor } from "./StockIcon";
import { entrantInfo } from "../util/characterColors";
import { buildSetListData, type SetData } from "../util/setData";
import { groupByPhase, setBracketLabel, type SetGroup } from "../../shared/setGroups";
import { TournamentCardBody } from "./Tournaments";
import { usePlayerLookup } from "../hooks/usePlayerLookup";

export type { SetData };

// --- TournamentSetList ---
// Renders a single tournament's set list. Stays mounted (display:none) when
// not visible so switching back is instant. Player-search filtering only
// updates while visible, so typing doesn't re-render hidden lists.

interface TournamentSetListProps {
  tournament: Tournament;
  playerQuery: string;
  selectedSetId: string | null;
  onSelectSet: (id: string) => void;
  onOpenPlayer: (publicId: string) => void;
  visible: boolean;
  /** Forwards this tournament's player lookup to a parent that needs it too
   *  (e.g. the catalog's SetInfo panel) - avoids a second, redundant fetch for
   *  names this component already resolved. */
  onPlayerLookup?: (lookup: PlayerLookupResult) => void;
}

export const TournamentSetList = memo(function TournamentSetList({
  tournament,
  playerQuery,
  selectedSetId,
  onSelectSet,
  onOpenPlayer,
  visible,
  onPlayerLookup,
}: TournamentSetListProps) {
  const [appliedQuery, setAppliedQuery] = useState(playerQuery);
  if (visible && appliedQuery !== playerQuery) {
    setAppliedQuery(playerQuery);
  }

  const sets = useMemo(
    () => buildSetListData(tournament, appliedQuery),
    [tournament, appliedQuery],
  );

  // Which of this tournament's raw entrant names already resolve to a
  // canonical player page - same lookup FocusedTournament's Overview tab
  // uses, so set rows on the home screen's catalog are clickable too.
  const lookupNames = useMemo(
    () =>
      Array.from(
        new Set(
          tournament.sets.flatMap((s) => [
            ...s.context.players.entrant1.map((p) => p.name),
            ...s.context.players.entrant2.map((p) => p.name),
          ]),
        ),
      ),
    [tournament],
  );
  const playerLookup = usePlayerLookup(tournament.folder, lookupNames);
  useEffect(() => {
    onPlayerLookup?.(playerLookup);
  }, [playerLookup, onPlayerLookup]);

  return (
    <div style={{ display: visible ? undefined : "none" }}>
      {sets.length === 0 ? (
        <p className="text-muted py-3 text-center">No matching sets</p>
      ) : (
        <SetList
          sets={sets}
          selectedSetId={selectedSetId}
          onSelectSet={onSelectSet}
          playerLookup={playerLookup}
          onOpenPlayer={onOpenPlayer}
        />
      )}
    </div>
  );
});

// --- SetList ---

interface SetListProps {
  sets: SetData[];
  selectedSetId: string | null;
  onSelectSet: (id: string) => void;
  /** Set to show a tournament link - only meaningful for a cross-tournament list. */
  onOpenTournament?: (folder: string) => void;
  /** Raw entrant name → canonical player identity (or null if unresolved), for clickable names. */
  playerLookup?: PlayerLookupResult;
  onOpenPlayer?: (publicId: string) => void;
  /** How to group sets into accordion sections. Defaults to "event" (existing behavior). */
  groupBy?: "event" | "tournament";
}

export const SetList = memo(function SetList({
  sets,
  selectedSetId,
  onSelectSet,
  onOpenTournament,
  playerLookup,
  onOpenPlayer,
  groupBy = "event",
}: SetListProps) {
  // One section per bracket: pools, gold and silver stay separate. A
  // cross-tournament list sections by tournament instead, since its sets span
  // brackets that have nothing to do with each other.
  const groups = useMemo(() => {
    if (groupBy !== "tournament") return groupByPhase(sets, (s) => s);
    const m = new Map<string, SetGroup<SetData>>();
    for (const s of sets) {
      const key = s.tournamentFolder ?? "Unknown tournament";
      const existing = m.get(key);
      if (existing) {
        existing.items.push(s);
        if (s.startMs < existing.firstMs) existing.firstMs = s.startMs;
      } else {
        m.set(key, { key, label: key, items: [s], firstMs: s.startMs });
      }
    }
    return Array.from(m.values());
  }, [sets, groupBy]);

  // Which tournament groups are collapsed - starts empty (all expanded), same
  // default as the event-name Accordion below.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (groups.length <= 1) {
    // Flat stack; cards name their own bracket with no heading to carry it.
    return (
      <div className="d-flex flex-column gap-2">
        {sets.map((s) => (
          <SetCard
            key={s.id}
            set={s}
            isSelected={s.id === selectedSetId}
            onSelect={onSelectSet}
            showLabel
            onOpenTournament={onOpenTournament}
            playerLookup={playerLookup}
            onOpenPlayer={onOpenPlayer}
          />
        ))}
      </div>
    );
  }

  if (groupBy === "tournament") {
    return (
      <div className="d-flex flex-column gap-3">
        {groups.map((group) => {
          const name = group.key;
          const groupSets = group.items;
          const first = groupSets[0];
          const collapsed = collapsedGroups.has(name);
          return (
            <div key={name}>
              <Card
                role="button"
                tabIndex={0}
                className="p-2 mb-2"
                onClick={() => toggleGroup(name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleGroup(name);
                  }
                }}
              >
                <Card.Body className="p-0 d-flex align-items-start gap-2">
                  <div className="flex-fill">
                    <TournamentCardBody
                      tournament={{
                        name,
                        location: first.startgg.tournament.location,
                        date: new Date(first.startMs).toLocaleDateString(),
                        setCount: groupSets.length,
                        slug: first.tournamentSlug ?? "",
                      }}
                      onOpenPage={() => onOpenTournament?.(name)}
                    />
                  </div>
                  <i
                    className={`bi bi-chevron-${collapsed ? "down" : "up"} text-body-secondary mt-1`}
                  />
                </Card.Body>
              </Card>
              {!collapsed && (
                <div className="d-flex flex-column gap-2">
                  {groupSets.map((s) => (
                    <SetCard
                      key={s.id}
                      set={s}
                      isSelected={s.id === selectedSetId}
                      onSelect={onSelectSet}
                      showLabel
                      playerLookup={playerLookup}
                      onOpenPlayer={onOpenPlayer}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Accordion alwaysOpen defaultActiveKey={groups.map((g) => g.key)} flush>
      {groups.map((group) => (
        <Accordion.Item eventKey={group.key} key={group.key}>
          <Accordion.Header>
            <span className="flex-fill">{group.label}</span>
            <Badge bg="secondary" className="me-2">
              {group.items.length}
            </Badge>
          </Accordion.Header>
          <Accordion.Body className="p-2">
            <div className="d-flex flex-column gap-2">
              {group.items.map((s) => (
                <SetCard
                  key={s.id}
                  set={s}
                  isSelected={s.id === selectedSetId}
                  onSelect={onSelectSet}
                  onOpenTournament={onOpenTournament}
                  playerLookup={playerLookup}
                  onOpenPlayer={onOpenPlayer}
                />
              ))}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  );
});

// --- SetCard ---

interface SetCardProps {
  set: SetData;
  isSelected: boolean;
  onSelect: (id: string) => void;
  /** Name the bracket on the card. Off when a group heading already does. */
  showLabel?: boolean;
  onOpenTournament?: (folder: string) => void;
  playerLookup?: PlayerLookupResult;
  onOpenPlayer?: (publicId: string) => void;
}

const SetCard = memo(function SetCard({
  set,
  isSelected,
  onSelect,
  showLabel,
  onOpenTournament,
  playerLookup,
  onOpenPlayer,
}: SetCardProps) {
  const p1 = set.players.entrant1[0];
  const p2 = set.players.entrant2[0];
  const p1Slot = set.finalScore.slots.find((s) => s.displayNames.includes(p1?.name ?? ""));
  const p2Slot = set.finalScore.slots.find((s) => s.displayNames.includes(p2?.name ?? ""));
  const score1 = p1Slot?.score ?? 0;
  const score2 = p2Slot?.score ?? 0;

  const p1Info = entrantInfo(p1Slot, set.gameSummaries);
  const p2Info = entrantInfo(p2Slot, set.gameSummaries);
  const p1Display: CharacterColor[] =
    p1Info.characters.length > 0 ? p1Info.characters : p1.characters.map((name) => ({ name }));
  const p2Display: CharacterColor[] =
    p2Info.characters.length > 0 ? p2Info.characters : p2.characters.map((name) => ({ name }));
  const bracket = showLabel ? setBracketLabel(set.startgg) : null;

  return (
    <Card
      role="button"
      tabIndex={0}
      className={`p-2 ${isSelected ? "is-selected" : ""}`}
      onClick={() => onSelect(set.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(set.id);
        }
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <h5 className="mb-2">
          {set.startgg.set.fullRoundText}
          {bracket && <small className="text-muted ms-2">{bracket}</small>}
          {set.tournamentFolder && onOpenTournament && (
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2 align-baseline"
              onClick={(e) => {
                e.stopPropagation();
                onOpenTournament(set.tournamentFolder!);
              }}
            >
              <small>{set.tournamentFolder}</small>
            </Button>
          )}
        </h5>
        <div className="d-flex align-items-center gap-2">
          <OverlayTrigger
            placement="top"
            popperConfig={{ strategy: "fixed" }}
            overlay={(props) => (
              <Tooltip id={`startgg-tooltip-${set.id}`} {...props}>
                View on start.gg (opens in new tab)
              </Tooltip>
            )}
          >
            <Button
              as="a"
              href={`https://www.start.gg/${set.startgg.event.slug}/set/${set.startgg.set.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center gap-1 py-0"
            >
              start.gg
              <i className="bi bi-box-arrow-up-right" />
            </Button>
          </OverlayTrigger>
          {set.videoUrl && <i className="bi bi-youtube text-danger" />}
        </div>
      </div>
      <div>
        <SetRow
          score={score1}
          name={p1.name}
          characters={p1Display}
          won={score1 > score2}
          publicId={playerLookup?.[p1.name]}
          onOpenPlayer={onOpenPlayer}
        />
        <hr className="my-1 opacity-25" />
        <SetRow
          score={score2}
          name={p2.name}
          characters={p2Display}
          won={score2 > score1}
          publicId={playerLookup?.[p2.name]}
          onOpenPlayer={onOpenPlayer}
        />
      </div>
    </Card>
  );
});

// --- PlayerNameLink ---
// A player name, made clickable to their canonical page when resolved via a
// playerLookup - shared by SetRow here and SetInfo's PlayerTag.

export function PlayerNameLink({
  name,
  publicId,
  onOpenPlayer,
}: {
  name: string;
  publicId?: string | null;
  onOpenPlayer?: (publicId: string) => void;
}) {
  if (!publicId || !onOpenPlayer) return <>{name}</>;
  return (
    <a
      href={`/player/${publicId}`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        onOpenPlayer(publicId);
      }}
      className="link-body-emphasis link-underline-opacity-0 link-underline-opacity-75-hover"
      title="Open player page"
    >
      {name}
    </a>
  );
}

interface SetRowProps {
  score: number;
  name: string;
  characters: CharacterColor[];
  won: boolean;
  publicId?: string | null;
  onOpenPlayer?: (publicId: string) => void;
}

function SetRow({ score, name, characters, won, publicId, onOpenPlayer }: SetRowProps) {
  return (
    <div className="d-flex align-items-center gap-2">
      <Badge pill bg={won ? "success" : "danger"} className="fs-6">
        {score}
      </Badge>
      <div className={`flex-fill text-start ${won ? "fw-bold" : ""}`}>
        <PlayerNameLink name={name} publicId={publicId} onOpenPlayer={onOpenPlayer} />
      </div>
      <StockIcons characters={characters} size={24} />
    </div>
  );
}
